import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../client";
import type {
  LocationPersonnel, Participation, PersonnelDocumentStatusList,
} from "../types";

const KEY = ["participations"] as const;

export function useParticipation(id: string | undefined) {
  return useQuery<Participation>({
    queryKey: [...KEY, id],
    queryFn: () => apiRequest(`/api/participations/${id}`),
    enabled: Boolean(id),
  });
}

export function useLocationParticipations(locationId: string | undefined) {
  return useQuery<Participation[]>({
    queryKey: ["locations", locationId, "participations"],
    queryFn: () => apiRequest(`/api/locations/${locationId}/participations`),
    enabled: Boolean(locationId),
  });
}

// Roster confirmed via the delegation-log review flow for this
// Participation. Enabled-gating lets callers defer the fetch until the
// user actually wants to see it (e.g. clicking expand on a card).
export function useParticipationPersonnel(
  participationId: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<LocationPersonnel[]>({
    queryKey: [...KEY, participationId, "personnel"],
    queryFn: () =>
      apiRequest(`/api/participations/${participationId}/personnel`),
    enabled: Boolean(participationId) && (opts.enabled ?? true),
  });
}

interface ForceActivateResult {
  participation_id: string;
  current_phase: number;
  status: string;
  activated_at: string | null;
  changed: boolean;
}

/** Required documents + per-doc status for one LocationPersonnel row.
 * Backed by GET /api/location-personnel/{id}/document-status — the
 * server unions role-based + task-based requirements and looks up the
 * most-recent Document attached to this personnel row (or its linked
 * Investigator, if any). Used by the per-person doc-status modal. */
export function usePersonnelDocumentStatus(
  personnelId: string | null,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<PersonnelDocumentStatusList>({
    queryKey: ["location-personnel", personnelId, "document-status"],
    queryFn: () =>
      apiRequest(`/api/location-personnel/${personnelId}/document-status`),
    enabled: Boolean(personnelId) && (opts.enabled ?? true),
  });
}

// Temporary admin convenience: jump a MASTER (cohort) participation
// straight to phase 4 / ACTIVATED, bypassing the task-status gates.
// Used by the "Activate cohort" button on each location row until the
// proper task-status workflow UI ships.
export function useForceActivateCohort(siteId: string, daCode: string) {
  const qc = useQueryClient();
  return useMutation<ForceActivateResult, Error, string>({
    mutationFn: (participationId) =>
      apiRequest(
        `/api/participations/${participationId}/force-activate-cohort`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sites", siteId, "clinops", daCode] });
    },
  });
}
